import React from 'react';
import { Formik } from 'formik';
import { Col, DatePicker, Row, Tooltip } from 'antd';
import { Form, FormItem, Input, ResetButton, SubmitButton, Radio, Select } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment/moment';
import isEmpty from 'lodash/isEmpty';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import Yup from '../../../../vendor/yup';
import { DATE_FORMAT, SEMVER_REGEX, targetPlatformOptions } from '../../../../const/system';
import useDatepickerHandler from '../../../../app/hooks/useDatepickerHandler';
import CountrySelector from '../../../../components/CountrySelector';
import { countries } from '../../../../const/countries';
import { isSmallerThan } from '../../../../const/targetValidation';
import { FEATURE_SHOW_UTC_FROM_TO } from '../../../../const/features';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import GetLanguagesOptions from '../../../../lib/getLanguagesOptions';
import getUtcOptions from '../../../../lib/getUtcOptions';



const validationSchema = Yup.lazy((values) => {
  return Yup.object().shape({
    id: Yup.string(),
    is_published: Yup.string().nullable(),
    scheduled_from: Yup.string().test(
      'test_date_after',
      'Scheduled from date must be same or before than scheduled to',
      (value) => {
        if (!isEmpty(values?.scheduled_to) && !isEmpty(value)) {
          return moment(values?.scheduled_to).isSameOrAfter(value);
        }
        return true;
      },
    ),
    scheduled_to: Yup.string().test(
      'test_date_before',
      'Scheduled to date must be same or after than scheduled at',
      (value) => {
        if (!isEmpty(values?.scheduled_from) && !isEmpty(value)) {
          return moment(values?.scheduled_from).isSameOrBefore(value);
        }
        return true;
      },
    ),
    min_version: Yup.string()
      .nullable()
      .matches(
        SEMVER_REGEX,
        'Min version has incorrect format. Try semantic version like 1.0 or 1.0.0',
      )
      .test(
        'check-min-version',
        'Min version should be less than or equal to the max version',
        (value) => {
          const maxValue = values.max_version;

          if (value && maxValue) {
            return isSmallerThan(value, maxValue) || value === maxValue;
          }

          return true;
        },
      ),
    max_version: Yup.string()
      .nullable()
      .matches(
        SEMVER_REGEX,
        'Max version has incorrect format. Try semantic version like 1.0 or 1.0.0',
      )
      .test(
        'check-max-version',
        'Max version should be greater than or equal to the min version',
        (value) => {
          const minValue = values.min_version;

          if (value && minValue) {
            return isSmallerThan(minValue, value) || value === minValue;
          }

          return true;
        },
      ),
    use_utc_zero_time: Yup.string().nullable(),
  });
});

const FormNotificationsScheduleFilter = ({
  initialValues,
  formErrors = {},
  languages = [],
  pushNotificationOptions = [],
  onSubmit,
  onSearchByTitle,
  onCancel,
  isSubmitting,
}) => {
  const intl = useIntl();
  const { onBlur } = useDatepickerHandler();

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={formErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      {({ isValid, dirty, values, setFieldValue, setFieldTouched }) => {
        return (
          <Form layout="vertical" >
            <FormItem
              name='id'
              label={
                <Row className='w-full' justify="space-between" align="middle">
                  <IntlMessages id="ui-general-id" />
                  <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                    <InfoCircleOutlined className="hp-ml-8" />
                  </Tooltip>
                </Row>
              }
            >
              <Input
                name="id"
                placeholder={intl.formatMessage({ id: 'push-notifications-schedule-id' })}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'local-notifications-form-title-placeholder' })}
              name='push_notification'
            >
              <Select
                mode="multiple"
                allowClear
                name="push_notification"
                options={pushNotificationOptions}
                placeholder={intl.formatMessage({ id: 'local-notifications-form-title-placeholder' })}
                onSearch={onSearchByTitle}
                filterOption={(input, option) => makeSelectFilterOption(option.title, input)}
              />
            </FormItem>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-filter-scheduled-at' })}
                  name='scheduled_from'
                >
                  <DatePicker
                    name='scheduled_from'
                    format={DATE_FORMAT}
                    showToday
                    value={values?.scheduled_from ? moment(values.scheduled_from) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('scheduled_from', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await onBlur('scheduled_from', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-filter-scheduled-to' })}
                  name='scheduled_to'
                >
                  <DatePicker
                    name='scheduled_to'
                    format={DATE_FORMAT}
                    value={values?.scheduled_to ? moment(values.scheduled_to) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('scheduled_to', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await onBlur('scheduled_to', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-published' })}
                  name='is_published'
                >
                  <Radio.Group
                    size='small'
                    name='is_published'
                    defaultValue={null}
                  >
                    <Radio.Button value={null}>{intl.formatMessage({ id: 'ui-general-all' })}</Radio.Button>
                    <Radio.Button value={1}>{intl.formatMessage({ id: 'ui-general-yes' })}</Radio.Button>
                    <Radio.Button value={0}>{intl.formatMessage({ id: 'ui-general-no' })}</Radio.Button>
                  </Radio.Group>
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={24}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-countries' })}
                  name='countries'
                >
                  <CountrySelector
                    onSelectAll={async () => {
                      await setFieldValue('countries', countries.map((country) => country.value).sort());
                    }}
                    isDisabledSelectAll
                    isView
                    setFieldValue={setFieldValue}
                    name='countries'
                    placeholder={intl.formatMessage({ id: 'ui-general-choose-countries' })}
                    setFieldTouched={setFieldTouched}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-min-version' })}
                  name='min_version'
                >
                  <Input
                    name="min_version"
                    placeholder={intl.formatMessage({ id: 'ui-general-targets-min-version-placeholder' })}
                  />
                </FormItem>
              </Col>

              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-max-version' })}
                  name='max_version'
                >
                  <Input
                    name="max_version"
                    placeholder={intl.formatMessage({ id: 'ui-general-targets-max-version-placeholder' })}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={24}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-platform' })}
                  name='platforms'
                >
                  <Select
                    mode="multiple"
                    allowClear
                    name="platforms"
                    options={targetPlatformOptions}
                    placeholder={intl.formatMessage({ id: 'ui-general-choose-platform' })}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={24}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-languages' })}
                  name='languages'
                >
                  <Select
                    mode="multiple"
                    allowClear
                    name="languages"
                    options={GetLanguagesOptions(languages, true)}
                    placeholder={intl.formatMessage({ id: 'ui-general-choose-languages' })}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-use-utc' })}
                  name='use_utc_zero_time'
                >
                  <Radio.Group
                    size='small'
                    name='use_utc_zero_time'
                    defaultValue={null}
                  >
                    <Radio.Button value={null}>{intl.formatMessage({ id: 'ui-general-all' })}</Radio.Button>
                    <Radio.Button value={1}>{intl.formatMessage({ id: 'ui-general-yes' })}</Radio.Button>
                    <Radio.Button value={0}>{intl.formatMessage({ id: 'ui-general-no' })}</Radio.Button>
                  </Radio.Group>
                </FormItem>
              </Col>
            </Row>
            {FEATURE_SHOW_UTC_FROM_TO && (
              <Row gutter={[ 16, 16 ]} justify='start'>
                <Col span={12}>
                  <FormItem
                    label={intl.formatMessage({ id: 'ui-general-utc-from' })}
                    name='utc_from'
                  >
                    <Select
                      allowClear
                      name="utc_from"
                      options={getUtcOptions()}
                      placeholder={intl.formatMessage({ id: 'ui-general-utc-from' })}
                    />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem
                    label={intl.formatMessage({ id: 'ui-general-utc-to' })}
                    name='utc_to'
                  >
                    <Select
                      allowClear
                      name="utc_to"
                      options={getUtcOptions()}
                      placeholder={intl.formatMessage({ id: 'ui-general-utc-to' })}
                    />
                  </FormItem>
                </Col>
              </Row>
            )}
            <Row gutter={[ 16, 16 ]} justify='end'>
              <Col>
                <ResetButton disabled={false}>
                  <IntlMessages id='ui-general-reset' />
                </ResetButton>
              </Col>

              <Col>
                <SubmitButton
                  type="primary"
                  loading={isSubmitting}
                  disabled={!isValid || !dirty}
                >
                  <IntlMessages id="ui-general-apply" />
                </SubmitButton>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormNotificationsScheduleFilter.propTypes = {
  initialValues: PropTypes.object.isRequired,
  languages: PropTypes.array.isRequired,
  pushNotificationOptions: PropTypes.array.isRequired,
  formErrors: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onSearchByTitle: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormNotificationsScheduleFilter;
